import React from 'react';
import { Info, Save } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Общие поля формы для вкладок раздела «Игры»: спарринг и лимиты правятся
 * одинаковыми контролами, дублировать их по вкладкам незачем.
 */
export function Card({
  isDark,
  title,
  icon,
  children,
  dimmed,
}: {
  isDark: boolean;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  dimmed?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl p-5 border',
        isDark
          ? 'bg-[#111111] border-slate-700'
          : 'bg-white border-slate-200 shadow-sm',
        dimmed && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-3 mb-4">
        {icon && (
          <div
            className={cn(
              'w-9 h-9 rounded-xl flex items-center justify-center',
              isDark
                ? 'bg-slate-800 text-amber-400'
                : 'bg-amber-50 text-amber-600',
            )}
          >
            {icon}
          </div>
        )}
        <h2
          className={cn(
            'text-lg font-semibold',
            isDark ? 'text-white' : 'text-[#111111]',
          )}
        >
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

export function TextField({
  isDark,
  label,
  value,
  onChange,
}: {
  isDark: boolean;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span
        className={cn(
          'block text-xs mb-1',
          isDark ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'w-full px-3 py-2 rounded-lg text-sm border outline-none',
          isDark
            ? 'bg-slate-900 border-slate-700 text-white'
            : 'bg-white border-slate-200 text-slate-900',
        )}
      />
    </label>
  );
}

export function NumberField({
  isDark,
  label,
  hint,
  value,
  min,
  max,
  onChange,
}: {
  isDark: boolean;
  label: string;
  hint?: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block">
      <span
        className={cn(
          'block text-xs mb-1',
          isDark ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {label}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isNaN(next)) return;
          onChange(next);
        }}
        className={cn(
          'w-full px-3 py-2 rounded-lg text-sm border outline-none',
          isDark
            ? 'bg-slate-900 border-slate-700 text-white'
            : 'bg-white border-slate-200 text-slate-900',
        )}
      />
      {hint && (
        <span
          className={cn(
            'block text-[11px] mt-1',
            isDark ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}

export function Toggle({
  isDark,
  label,
  hint,
  value,
  onChange,
}: {
  isDark: boolean;
  label: string;
  hint?: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div>
      <button
        onClick={() => onChange(!value)}
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg border w-full text-left transition',
          isDark
            ? 'bg-slate-900 border-slate-700 hover:bg-slate-800'
            : 'bg-white border-slate-200 hover:bg-slate-50',
        )}
      >
        <span
          className={cn(
            'w-9 h-5 rounded-full relative transition shrink-0',
            value ? 'bg-emerald-500' : isDark ? 'bg-slate-700' : 'bg-slate-300',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
              value ? 'left-[18px]' : 'left-0.5',
            )}
          />
        </span>
        <span
          className={cn(
            'text-sm',
            isDark ? 'text-slate-200' : 'text-slate-700',
          )}
        >
          {label}
        </span>
      </button>
      {hint && (
        <span
          className={cn(
            'block text-[11px] mt-1',
            isDark ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

export function Stat({
  isDark,
  label,
  value,
  hint,
  tooltip,
  tone = 'slate',
}: {
  isDark: boolean;
  label: string;
  value: string;
  /** Короткая подпись под числом. */
  hint?: string;
  /** Развёрнутое пояснение по наведению — как читать эту метрику. */
  tooltip?: string;
  tone?: 'slate' | 'emerald' | 'red';
}) {
  const toneClass =
    tone === 'emerald'
      ? 'text-emerald-500'
      : tone === 'red'
        ? 'text-red-500'
        : isDark
          ? 'text-white'
          : 'text-slate-900';

  return (
    <div
      className={cn(
        'rounded-xl px-4 py-3 border',
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200',
      )}
    >
      <div
        className={cn(
          'text-xs',
          isDark ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {label}
        {tooltip && <InfoHint isDark={isDark} text={tooltip} />}
      </div>
      <div className={cn('text-lg font-semibold', toneClass)}>{value}</div>
      {hint && (
        <div
          className={cn(
            'text-[11px]',
            isDark ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

export function SaveButton({
  isDark,
  saving,
  onClick,
}: {
  isDark: boolean;
  saving: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition',
        'bg-[#C9A227] text-white hover:opacity-90 disabled:opacity-50',
      )}
    >
      <Save className="w-4 h-4" />
      {saving ? 'Сохраняю…' : 'Сохранить'}
    </button>
  );
}

/**
 * Подсказка у метрики или настройки. Своя, а не Radix: тултипу нужен
 * провайдер на всё приложение, а здесь достаточно hover без порталов.
 * `title` оставлен как запасной путь для тач-устройств.
 */
export function InfoHint({ isDark, text }: { isDark: boolean; text: string }) {
  return (
    <span
      className="relative inline-flex group align-middle ml-1.5"
      tabIndex={0}
      title={text}
    >
      <Info
        className={cn(
          'w-3.5 h-3.5 cursor-help transition',
          isDark
            ? 'text-slate-500 group-hover:text-slate-300'
            : 'text-slate-400 group-hover:text-slate-600',
        )}
      />
      <span
        className={cn(
          'pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2',
          'w-60 rounded-lg px-3 py-2 text-xs leading-snug text-left font-normal normal-case',
          'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
          'group-focus:opacity-100 group-focus:visible transition duration-150 z-50 shadow-xl',
          isDark
            ? 'bg-slate-800 text-slate-100 border border-slate-700'
            : 'bg-slate-900 text-white',
        )}
      >
        {text}
      </span>
    </span>
  );
}

/**
 * Шапка вкладки: чем этот раздел управляет и как читать цифры.
 * Нужна, чтобы админ, зашедший впервые, не гадал по названиям колонок.
 */
export function TabIntro({
  isDark,
  children,
  tone = 'neutral',
}: {
  isDark: boolean;
  children: React.ReactNode;
  tone?: 'neutral' | 'warning';
}) {
  return (
    <div
      className={cn(
        'rounded-xl px-4 py-3 text-sm leading-relaxed border',
        tone === 'warning'
          ? isDark
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
            : 'bg-amber-50 border-amber-200 text-amber-900'
          : isDark
            ? 'bg-slate-900 border-slate-800 text-slate-300'
            : 'bg-slate-50 border-slate-200 text-slate-600',
      )}
    >
      {children}
    </div>
  );
}
